import { type SideNavigationTabType } from "@validereinc/common-components";
import { AppRegistrySchema } from "@validereinc/domain";
import { uniq } from "es-toolkit/compat";

/** The custom apps the web app knows how to link to. Values are the names Carbon Hub's app registry uses. */
export const CustomAppName = {
  AMMONIA: "ammonia",
} as const;

export type CustomAppNameType =
  (typeof CustomAppName)[keyof typeof CustomAppName];

export type CustomAppDefinitionType = {
  title: string;
  icon: NonNullable<SideNavigationTabType["icon"]>;
  description: string;
  urls: {
    staging: string;
    production: string;
  };
};

/** How each custom app is presented in the web app, keyed by its name in the app registry. */
export const CUSTOM_APP_DEFINITION: Record<
  CustomAppNameType,
  CustomAppDefinitionType
> = {
  [CustomAppName.AMMONIA]: {
    title: "Ammonia",
    icon: "atom",
    description: "Open Ammonia management application in a new tab.",
    urls: {
      staging: "https://ammonia.labs.validere.xyz",
      production: "https://ammonia.labs.validere.com",
    },
  },
};

/** The `ENV` values the web app is built with when it is the production deployment. Mirrors the production check in the webpack config. */
const PRODUCTION_ENVIRONMENTS: readonly string[] = ["production", "prod"];

export type CustomAppEntryType = Pick<
  CustomAppDefinitionType,
  "title" | "icon" | "description"
> & {
  name: CustomAppNameType;
  url: string;
};

/** Is this app name from the app registry one that the web app has a definition for? */
export const isCustomAppName = (
  appName: string
): appName is CustomAppNameType =>
  (Object.values(CustomAppName) as string[]).includes(appName);

/**
 * Pick the URL of a custom app for the environment the web app is running in.
 * Anything that isn't production (local, preview, test) gets the staging URL so
 * a non-production build can never send a user to the production app.
 */
export const getCustomAppUrl = (
  definition: CustomAppDefinitionType,
  environment: string | undefined
): string =>
  environment && PRODUCTION_ENVIRONMENTS.includes(environment)
    ? definition.urls.production
    : definition.urls.staging;

/**
 * Read the accessible app names out of an app registry response. Anything that
 * doesn't match the expected shape yields no apps rather than an error.
 */
export const getAccessibleCustomAppNames = (appRegistry: unknown): string[] => {
  const parsedAppRegistry = AppRegistrySchema.safeParse(appRegistry);

  return parsedAppRegistry.success ? parsedAppRegistry.data.apps : [];
};

/**
 * Map the app names from the app registry to the entries to present, in the
 * registry's order. Names without a definition are ignored.
 */
export const getCustomAppEntries = ({
  registeredAppNames,
  environment,
}: {
  registeredAppNames: string[];
  environment: string | undefined;
}): CustomAppEntryType[] =>
  uniq(registeredAppNames)
    .filter(isCustomAppName)
    .map((name) => {
      const definition = CUSTOM_APP_DEFINITION[name];

      return {
        name,
        title: definition.title,
        icon: definition.icon,
        description: definition.description,
        url: getCustomAppUrl(definition, environment),
      };
    });
